import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Check, X, Trash2, Eye, AlertTriangle, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// StarRating component defined outside AdminReviews
const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        size={16}
        className={rating >= star ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#57534E]'}
      />
    ))}
  </div>
);

const AdminReviews = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved
  const [selectedReview, setSelectedReview] = useState(null);

  const adminToken = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    loadReviews();
  }, [adminToken, filter]);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const pendingOnly = filter === 'pending';
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/reviews?pending_only=${pendingOnly}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      let filtered = response.data.reviews;
      if (filter === 'approved') {
        filtered = filtered.filter(r => r.is_approved);
      } else if (filter === 'pending') {
        filtered = filtered.filter(r => !r.is_approved);
      }
      
      setReviews(filtered);
    } catch (error) {
      console.error('Error loading reviews:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId, approve) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/reviews/${reviewId}/approve`,
        approve,
        { 
          headers: { 
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      toast.success(approve ? 'Review goedgekeurd' : 'Review afgekeurd');
      loadReviews();
    } catch (error) {
      toast.error('Actie mislukt');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Weet je zeker dat je deze review wilt verwijderen?')) return;
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/reviews/${reviewId}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Review verwijderd');
      loadReviews();
      setSelectedReview(null);
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/10 text-yellow-500',
      processing: 'bg-blue-500/10 text-blue-500',
      shipped: 'bg-purple-500/10 text-purple-500',
      delivered: 'bg-green-500/10 text-green-500',
      cancelled: 'bg-red-500/10 text-red-500'
    };
    return colors[status] || 'bg-gray-500/10 text-gray-500';
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#1C1917] border-b border-[#2C1B18] px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl text-white">Review Beheer</h1>
            <p className="text-[#A8A29E] text-sm">Beheer en modereer klantbeoordelingen</p>
          </div>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="btn-secondary text-sm"
          >
            ← Terug naar Dashboard
          </button>
        </div>
      </header>

      <div className="p-6">
        {/* Filter Bar */}
        <div className="glass rounded-xl p-4 mb-6 flex flex-wrap items-center gap-4">
          <span className="text-[#A8A29E]">Filter:</span>
          <div className="flex gap-2">
            {[
              { id: 'all', label: 'Alle reviews' },
              { id: 'pending', label: 'Wacht op goedkeuring' },
              { id: 'approved', label: 'Goedgekeurd' }
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  filter === f.id
                    ? 'bg-[#D4AF37] text-black'
                    : 'bg-[#2C1B18] text-[#A8A29E] hover:text-white'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="text-[#57534E] ml-auto">
            {reviews.length} review{reviews.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto"></div>
          </div>
        ) : reviews.length === 0 ? (
          <div className="glass rounded-xl p-12 text-center">
            <AlertTriangle size={48} className="mx-auto text-[#57534E] mb-4" />
            <p className="text-[#A8A29E]">
              {filter === 'pending' ? 'Geen reviews wachten op goedkeuring' : 'Geen reviews gevonden'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div 
                key={review.id} 
                className={`glass rounded-xl p-6 border-l-4 ${
                  review.is_approved ? 'border-l-green-500' : 'border-l-yellow-500'
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  {/* Review Content */}
                  <div className="flex-1">
                    <div className="flex items-start gap-4 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-white font-medium">{review.user_name}</span>
                          {review.is_verified_purchase && (
                            <span className="text-green-500 text-xs flex items-center gap-1">
                              <Check size={12} /> Geverifieerde aankoop
                            </span>
                          )}
                          {!review.is_approved && (
                            <span className="text-yellow-500 text-xs bg-yellow-500/10 px-2 py-0.5 rounded">
                              Wacht op goedkeuring
                            </span>
                          )}
                        </div>
                        <StarRating rating={review.rating} />
                      </div>
                      <span className="text-[#57534E] text-sm whitespace-nowrap">
                        {formatDate(review.created_at)}
                      </span>
                    </div>
                    
                    {/* Product Info */}
                    {review.product && (
                      <div className="text-[#D4AF37] text-sm mb-2">
                        {review.product.brand} - {review.product.name}
                      </div>
                    )}
                    
                    <h4 className="text-white font-medium mb-2">{review.title}</h4>
                    <p className="text-[#A8A29E] text-sm">{review.content}</p>
                    
                    <div className="text-[#57534E] text-xs mt-2">
                      {review.helpful_votes} personen vonden dit nuttig
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex md:flex-col gap-2 flex-shrink-0">
                    {!review.is_approved ? (
                      <button
                        onClick={() => handleApprove(review.id, true)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30 transition-colors"
                        title="Goedkeuren"
                      >
                        <Check size={18} />
                        <span className="md:hidden">Goedkeuren</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleApprove(review.id, false)}
                        className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 text-yellow-500 rounded-lg hover:bg-yellow-500/30 transition-colors"
                        title="Intrekken"
                      >
                        <X size={18} />
                        <span className="md:hidden">Intrekken</span>
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedReview(review)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#2C1B18] text-[#A8A29E] rounded-lg hover:text-white transition-colors"
                      title="Bekijken"
                    >
                      <Eye size={18} />
                      <span className="md:hidden">Bekijken</span>
                    </button>
                    <button
                      onClick={() => handleDelete(review.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30 transition-colors"
                      title="Verwijderen"
                    >
                      <Trash2 size={18} />
                      <span className="md:hidden">Verwijderen</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Detail Modal */}
      {selectedReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="glass rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#2C1B18] flex items-center justify-between">
              <h2 className="font-heading text-xl text-white">Review Details</h2>
              <button
                onClick={() => setSelectedReview(null)}
                className="text-[#A8A29E] hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Status */}
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                selectedReview.is_approved 
                  ? 'bg-green-500/20 text-green-500' 
                  : 'bg-yellow-500/20 text-yellow-500'
              }`}>
                {selectedReview.is_approved ? <Check size={16} /> : <AlertTriangle size={16} />}
                {selectedReview.is_approved ? 'Goedgekeurd' : 'Wacht op goedkeuring'}
              </div>
              
              {/* Product */}
              {selectedReview.product && (
                <div className="glass p-4 rounded-lg">
                  <span className="text-[#A8A29E] text-sm">Product:</span>
                  <p className="text-white font-medium">
                    {selectedReview.product.brand} - {selectedReview.product.name}
                  </p>
                </div>
              )}
              
              {/* Reviewer */}
              <div className="flex items-center gap-4">
                <div>
                  <span className="text-[#A8A29E] text-sm">Reviewer:</span>
                  <p className="text-white font-medium">{selectedReview.user_name}</p>
                </div>
                {selectedReview.is_verified_purchase && (
                  <span className="text-green-500 text-xs flex items-center gap-1">
                    <Check size={14} /> Geverifieerde aankoop
                  </span>
                )}
              </div>
              
              {/* Rating */}
              <div>
                <span className="text-[#A8A29E] text-sm">Beoordeling:</span>
                <div className="flex items-center gap-2 mt-1">
                  <StarRating rating={selectedReview.rating} />
                  <span className="text-white font-bold">{selectedReview.rating}/5</span>
                </div>
              </div>
              
              {/* Content */}
              <div>
                <span className="text-[#A8A29E] text-sm">Review:</span>
                <h3 className="text-white font-medium text-lg mt-1">{selectedReview.title}</h3>
                <p className="text-[#A8A29E] mt-2 whitespace-pre-wrap">{selectedReview.content}</p>
              </div>
              
              {/* Date */}
              <div>
                <span className="text-[#A8A29E] text-sm">Geplaatst op:</span>
                <p className="text-white">{formatDate(selectedReview.created_at)}</p>
              </div>
              
              {/* Stats */}
              <div>
                <span className="text-[#A8A29E] text-sm">Statistieken:</span>
                <p className="text-white">{selectedReview.helpful_votes} nuttig stemmen</p>
              </div>
            </div>
            
            {/* Actions */}
            <div className="p-6 border-t border-[#2C1B18] flex gap-4">
              {!selectedReview.is_approved ? (
                <button
                  onClick={() => {
                    handleApprove(selectedReview.id, true);
                    setSelectedReview(null);
                  }}
                  className="btn-primary flex items-center gap-2"
                >
                  <Check size={18} />
                  Goedkeuren
                </button>
              ) : (
                <button
                  onClick={() => {
                    handleApprove(selectedReview.id, false);
                    setSelectedReview(null);
                  }}
                  className="btn-secondary flex items-center gap-2"
                >
                  <X size={18} />
                  Goedkeuring intrekken
                </button>
              )}
              <button
                onClick={() => handleDelete(selectedReview.id)}
                className="px-4 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-2"
              >
                <Trash2 size={18} />
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
